import { cn } from '@/lib/utils';
import type { NFTTier } from '@/types/schemas';

interface SeasonNFTCardProps {
  seasonNo: number;
  tier: NFTTier;
  rank: number | null;
  pointsContributed: string;
  nftName: string;
  nftMinted: boolean;
  onMintNFT: () => void;
  onNext: () => void;
  onBack?: () => void;
  className?: string;
}

// Tier utilities (matching NFTMintingModal)
function getTierImage(tier: NFTTier): string {
  const tierMap = {
    diamond: '/nfts/DiamondTierNFT_JunkFun.png',
    black: '/nfts/BlackTierNFT_JunkFun.png',
    gold: '/nfts/GoldTierNFT_JunkFun.png',
    silver: '/nfts/SilverTierNFT_JunkFun.png',
    bronze: '/nfts/BronzeTierNFT_JunkFun.png',
  };
  return tierMap[tier];
}

function getTierGradient(tier: NFTTier): string {
  const gradientMap = {
    diamond: 'linear-gradient(251deg, #50E4F9 10.86%, #DEFBFF 50.57%, #50E4F9 82.07%)',
    black: 'linear-gradient(251deg, #2C2C2C 10.86%, #6B6B6B 50.57%, #2C2C2C 82.07%)',
    gold: 'linear-gradient(251deg, #FFD700 10.86%, #FFF4A3 50.57%, #FFD700 82.07%)',
    silver: 'linear-gradient(251deg, #C0C0C0 10.86%, #E8E8E8 50.57%, #C0C0C0 82.07%)',
    bronze: 'linear-gradient(251deg, #CD7F32 10.86%, #F4A460 50.57%, #CD7F32 82.07%)',
  };
  return gradientMap[tier];
}

function getTierName(tier: NFTTier): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function SeasonNFTCard({
  seasonNo,
  tier,
  rank,
  nftName,
  nftMinted,
  onMintNFT,
  onNext,
  onBack,
  className,
}: SeasonNFTCardProps) {
  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={{
        width: 500,
        height: 620,
        background: '#1B1B1B',
        boxShadow: '5px 4px 50px rgba(0, 0, 0, 0.75)',
        border: '2px solid #50E4F9',
        borderRadius: 40,
      }}
    >
      {/* Background image */}
      <img
        src="/Plano Futurista em Ciano.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          zIndex: 0,
          borderRadius: 40,
        }}
      />

      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 z-20 p-2 rounded-full transition-all hover:scale-110 active:scale-95"
          style={{
            background: 'rgba(80, 228, 249, 0.2)',
            border: '2px solid #50E4F9',
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Go back"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="#50E4F9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center h-full px-6 py-5">
        {/* Header */}
        <h1
          className="text-center mb-2"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: 28,
            fontWeight: '700',
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}
        >
          Season #{seasonNo} NFT
        </h1>

        {/* Blue divider line - Full width */}
        <div
          className="absolute left-0 right-0"
          style={{
            top: 62,
            height: 2,
            background: '#50E4F9',
          }}
        />

        {/* Badges Row */}
        <div className="flex items-center justify-between w-full mb-3 px-1" style={{ marginTop: 18 }}>
          {/* Tier Badge */}
          <div
            className="px-4 py-1.5 rounded-lg"
            style={{
              background: getTierGradient(tier),
              boxShadow: '0 4px 15px rgba(80, 228, 249, 0.3)',
            }}
          >
            <span
              style={{
                fontFamily: 'Hyperspace Race Capsule, sans-serif',
                fontSize: 15,
                fontWeight: 'bold',
                color: tier === 'black' || tier === 'bronze' ? 'white' : '#1B1B1B',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {getTierName(tier)}
            </span>
          </div>

          {/* Rank Badge - Always show */}
          <div
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
            style={{
              border: '2px solid #50E4F9',
              background: 'rgba(80, 228, 249, 0.1)',
            }}
          >
            {/* Chart Icon */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 3V21H21"
                stroke="#50E4F9"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7 16L12 11L16 15L20 8"
                stroke="#50E4F9"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: 16,
                fontWeight: '700',
                color: '#50E4F9',
              }}
            >
              {rank !== null ? rank : '-'}
            </span>
          </div>
        </div>

        {/* NFT Image */}
        <div
          className="mb-3"
          style={{
            width: 300,
            height: 300,
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          }}
        >
          <img
            src={getTierImage(tier)}
            alt={`${getTierName(tier)} Tier NFT`}
            className="w-full h-full object-contain"
          />
        </div>

        {/* NFT Collection Label */}
        <div
          className="mb-0.5"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: 12,
            fontWeight: '400',
            color: '#50E4F9',
            letterSpacing: '1px',
          }}
        >
          NFT Collection
        </div>

        {/* NFT Name */}
        <div
          className="mb-3 text-center"
          style={{
            fontFamily: 'Outfit, sans-serif',
            fontSize: 18,
            fontWeight: '600',
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          {nftName}
        </div>

        {/* Bottom border separator - Full width */}
        <div
          className="absolute left-0 right-0 mt-auto"
          style={{
            bottom: 92,
            height: 2,
            background: '#50E4F9',
          }}
        />

        {/* Action Buttons */}
        <div className="flex items-center justify-center gap-3 mt-auto mb-3">
          {/* Mint NFT Button */}
          <button
            onClick={nftMinted ? undefined : onMintNFT}
            disabled={nftMinted}
            className={cn(
              'px-5 py-2.5 rounded-full transition-all',
              nftMinted
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:scale-105 active:scale-95'
            )}
            style={{
              background: nftMinted
                ? '#666666'
                : 'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              fontFamily: 'Hyperspace Race Capsule, sans-serif',
              fontSize: 15,
              fontWeight: 'bold',
              color: '#1B1B1B',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: nftMinted ? 'none' : '0 4px 20px rgba(251, 139, 0, 0.4)',
              minWidth: 160,
            }}
          >
            {nftMinted ? 'Already Minted' : 'Mint Your NFT'}
          </button>

          {/* Next Button */}
          <button
            onClick={onNext}
            className="px-5 py-2.5 rounded-full transition-all hover:scale-105 active:scale-95"
            style={{
              background: 'transparent',
              border: '2px solid white',
              fontFamily: 'Hyperspace Race Capsule, sans-serif',
              fontSize: 15,
              fontWeight: 'bold',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              minWidth: 120,
            }}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
